<template>
  <div class="contato-fidelizacao-form">
    <!-- AI-Powered Content Extraction -->
    <div class="form-section">
      <object-completion
        :label="t('Preencher com IA')"
        :description="t('Descreva o contato com o cliente')"
        :placeholder="t('Digite ou cole o texto do contato aqui...')"
        :get-json-schema-to-fill="getSchemaToFill"
        :extra-informations-object="getExtraInformations"
        :success-callback="handleAISuccess"
        :error-callback="handleAIError"
        :cancel-label="t('Cancelar')"
        :send-label="t('Extrair Informações')"
        :debug="false">
      </object-completion>
    </div>

    <!-- Client and Franchise Info (Read-only) -->
    <box
      type="primary"
      :title="t('Informações do contato')"
      elem-id="boxInfoContato">

      <div class="form-row">
        <div class="form-field-half">
          <input-text
            data-testid="contato-fidelizacao-cliente-input"
            elem-id="contato_fidelizacao_cliente_nome"
            :label="t('Cliente')"
            :disabled="true"
            v-model="contatoData.cliente_nome">
          </input-text>
        </div>
        <div class="form-field-half">
          <input-text
            data-testid="contato-fidelizacao-franquia-input"
            elem-id="contato_fidelizacao_franquia_nome"
            :label="t('Franquia')"
            :disabled="true"
            v-model="contatoData.contatofidelizacao_franquia_nome">
          </input-text>
        </div>
      </div>
    </box>

    <!-- Contact Information -->
    <box
      type="primary"
      :title="t('Informações do Contato')"
      elem-id="boxDetalhesContato">

      <div class="form-row">
        <div class="form-field-half">
          <select-list
            data-testid="contato-fidelizacao-owner-select"
            elem-id="contato_fidelizacao_owner"
            :label="t('Responsável pelo Contato')"
            :placeholder="t('Selecione o responsável')"
            :itens-select="ownerOptions"
            :validation="() => isOwnerValid"
            :required="true"
            v-model="contatoData.contatofidelizacao_owner">
          </select-list>
        </div>
        <div class="form-field-half">
          <input-date-time
            data-testid="contato-fidelizacao-data-input"
            elem-id="contato_fidelizacao_data_cadastro"
            :label="t('Data do Contato')"
            :validation="() => isDataValid"
            :required="true"
            v-model="contatoData.contatofidelizacao_data_cadastro">
          </input-date-time>
        </div>
      </div>

      <div class="form-field">
        <input-rich-text
          data-testid="contato-fidelizacao-content-input"
          :label="t('Conteúdo do Contato')"
          :placeholder="t('Descreva o conteúdo do contato com o cliente...')"
          :ng-required="true"
          :show-toolbar="true"
          min-height="200px"
          v-model="contatoData.contatofidelizacao_content">
        </input-rich-text>
        <small class="form-hint">{{ t('Descreva detalhadamente o que foi discutido ou acordado com o cliente.') }}</small>
      </div>
    </box>

  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useContatoFidelizacao } from '../../composables/useContatoFidelizacao';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { useCliente } from '../../composables/useCliente';
import { permissionsService } from '../../services/permissionsService';
import ObjectCompletion from '../../inputs/ObjectCompletion.vue';
import InputRichText from '../../inputs/InputRichText.vue';

type Id = number | string;

interface ContatoData {
  contatofidelizacao_id: Id | null;
  contatofidelizacao_data_cadastro: string;
  contatofidelizacao_franquia_id: number | null;
  contatofidelizacao_franquia_nome: string;
  contatofidelizacao_cliente_id: number | null;
  cliente_nome: string;
  contatofidelizacao_owner: string;
  contatofidelizacao_owner_id: string;
  contatofidelizacao_content: string;
}

interface OwnerOption {
  ID: string;
  Name: string;
}

interface Props {
  isEditMode?: boolean;
}

const { t } = useI18n();

const { fetchContatoFidelizacao, saveContatoFidelizacao } = useContatoFidelizacao();
// API → App: ISO 8601 UTC → hora local do navegador (o que o InputDateTime lê)
const { apiDateTimeToLocalInput, fromDateToApiDateTime } = useDateFormatter();
const { fetchCliente } = useCliente();

const props = withDefaults(defineProps<Props>(), {
  isEditMode: false
});

const emit = defineEmits<{
  'save-success': [result: unknown];
}>();

const isLoading = ref<boolean>(false);

const contatoData = reactive<ContatoData>({
  contatofidelizacao_id: null,
  contatofidelizacao_data_cadastro: '',
  contatofidelizacao_franquia_id: null,
  contatofidelizacao_franquia_nome: '',
  contatofidelizacao_cliente_id: null,
  cliente_nome: '',
  contatofidelizacao_owner: 'user',
  contatofidelizacao_owner_id: '',
  contatofidelizacao_content: ''
});

const ownerOptions = computed<OwnerOption[]>(() => [
  { ID: 'user', Name: t('Usuário') },
  { ID: 'system', Name: t('Sistema') }
]);

const isOwnerValid = computed<boolean>(() => {
  log('ContatoFidelizacaoForm', 'isOwnerValid', 'Checking owner', contatoData.contatofidelizacao_owner);
  return !!contatoData.contatofidelizacao_owner && contatoData.contatofidelizacao_owner !== '';
});

const isOwnerIdValid = computed<boolean>(() => {
  log('ContatoFidelizacaoForm', 'isOwnerIdValid', 'Checking owner_id', contatoData.contatofidelizacao_owner_id);
  return !!contatoData.contatofidelizacao_owner_id && contatoData.contatofidelizacao_owner_id !== '';
});

const isDataValid = computed<boolean>(() => {
  return !!contatoData.contatofidelizacao_data_cadastro && contatoData.contatofidelizacao_data_cadastro !== '';
});

const isContentValid = computed<boolean>(() => {
  return !!contatoData.contatofidelizacao_content && contatoData.contatofidelizacao_content.trim().length > 0;
});

const isFranquiaValid = computed<boolean>(() => {
  return !!contatoData.contatofidelizacao_franquia_id && contatoData.contatofidelizacao_franquia_id > 0;
});

const isClienteValid = computed<boolean>(() => {
  return !!contatoData.contatofidelizacao_cliente_id && contatoData.contatofidelizacao_cliente_id > 0;
});

const canSave = computed<boolean>(() => {
  const valid = isOwnerValid.value &&
         isOwnerIdValid.value &&
         isDataValid.value &&
         isContentValid.value &&
         isFranquiaValid.value &&
         isClienteValid.value &&
         !isLoading.value;
  log('ContatoFidelizacaoForm', 'canSave', 'Validation result', {
    valid,
    isOwnerValid: isOwnerValid.value,
    isOwnerIdValid: isOwnerIdValid.value,
    isDataValid: isDataValid.value,
    isContentValid: isContentValid.value,
    isFranquiaValid: isFranquiaValid.value,
    isClienteValid: isClienteValid.value,
    isLoading: isLoading.value
  });
  return valid;
});

watch(() => contatoData.contatofidelizacao_owner, (newOwner: string) => {
  log('ContatoFidelizacaoForm', 'watch:owner', 'Owner changed', newOwner);
  if (newOwner === 'user') {
    const user = permissionsService.getUser();
    log('ContatoFidelizacaoForm', 'watch:owner', 'Got user from permissions', user);
    contatoData.contatofidelizacao_owner_id = user?.sub || '';
  } else if (newOwner === 'system') {
    contatoData.contatofidelizacao_owner_id = 'system';
  }
  log('ContatoFidelizacaoForm', 'watch:owner', 'Updated owner_id', contatoData.contatofidelizacao_owner_id);
});

const loadClientInfo = async (clienteId: Id): Promise<void> => {
  log('ContatoFidelizacaoForm', 'loadClientInfo', 'Begin', clienteId);
  try {
    const cliente = await fetchCliente(Number(clienteId));
    log('ContatoFidelizacaoForm', 'loadClientInfo', 'Response', cliente);

    if (cliente) {
      contatoData.cliente_nome = cliente.cliente_nome || '';
      log('ContatoFidelizacaoForm', 'loadClientInfo', 'Loaded client name', contatoData.cliente_nome);
    }
  } catch (error) {
    log('ContatoFidelizacaoForm', 'loadClientInfo', 'Error', error);
    console.error('Error loading client info:', error);
  }
};

const loadForEdit = async (contatoId: Id): Promise<void> => {
  log('ContatoFidelizacaoForm', 'loadForEdit', 'Begin', contatoId);
  try {
    isLoading.value = true;

    const data = await fetchContatoFidelizacao(Number(contatoId));
    log('ContatoFidelizacaoForm', 'loadForEdit', 'Response', data);

    if (!data) return;

    Object.assign(contatoData, {
      contatofidelizacao_id: data.contatofidelizacao_id,
      contatofidelizacao_data_cadastro: apiDateTimeToLocalInput(data.contatofidelizacao_data_cadastro),
      contatofidelizacao_franquia_id: parseInt(String(data.contatofidelizacao_franquia_id)) || null,
      contatofidelizacao_franquia_nome: data.contatofidelizacao_franquia_nome || '',
      contatofidelizacao_cliente_id: parseInt(String(data.contatofidelizacao_cliente_id)) || null,
      contatofidelizacao_owner: data.contatofidelizacao_owner || 'user',
      contatofidelizacao_owner_id: data.contatofidelizacao_owner_id || '',
      contatofidelizacao_content: data.contatofidelizacao_content || ''
    });

    if (contatoData.contatofidelizacao_cliente_id) {
      await loadClientInfo(contatoData.contatofidelizacao_cliente_id);
    }

    log('ContatoFidelizacaoForm', 'loadForEdit', 'Loaded contact data', contatoData);

  } catch (error) {
    console.error('Error loading contato:', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', message);
  } finally {
    isLoading.value = false;
    log('ContatoFidelizacaoForm', 'loadForEdit', 'Finished');
  }
};

const initializeForNew = async (
  franquiaId: Id | null | undefined,
  clienteId: Id | null | undefined,
  clienteNome: string | null | undefined,
  franquiaNome: string | null | undefined
): Promise<void> => {
  log('ContatoFidelizacaoForm', 'initializeForNew', 'Begin', { franquiaId, clienteId, clienteNome, franquiaNome });

  resetForm();

  contatoData.contatofidelizacao_franquia_id = franquiaId != null ? (parseInt(String(franquiaId)) || null) : null;
  contatoData.contatofidelizacao_cliente_id = clienteId != null ? (parseInt(String(clienteId)) || null) : null;
  contatoData.cliente_nome = clienteNome || '';

  if (franquiaNome) {
    contatoData.contatofidelizacao_franquia_nome = franquiaNome;
  } else if (franquiaId) {
    const franchises = permissionsService.getFranquias() as Record<string, { Dados?: { Nome?: string } }> | null | undefined;
    log('ContatoFidelizacaoForm', 'initializeForNew', 'Getting franchise name from permissions', { franquiaId, franchises });
    const key = String(franquiaId);
    if (franchises && franchises[key]) {
      contatoData.contatofidelizacao_franquia_nome = franchises[key].Dados?.Nome || '';
      log('ContatoFidelizacaoForm', 'initializeForNew', 'Set franchise name', contatoData.contatofidelizacao_franquia_nome);
    }
  }

  contatoData.contatofidelizacao_owner = 'user';
  const user = permissionsService.getUser();
  log('ContatoFidelizacaoForm', 'initializeForNew', 'Got user from permissions', user);
  contatoData.contatofidelizacao_owner_id = user?.sub || '';

  setDefaultDate();

  if (!contatoData.cliente_nome && clienteId) {
    await loadClientInfo(clienteId);
  }

  log('ContatoFidelizacaoForm', 'initializeForNew', 'Initialized', contatoData);
};

const saveContato = async (): Promise<void> => {
  log('ContatoFidelizacaoForm', 'saveContato', 'Attempt save', contatoData);
  if (!canSave.value) {
    log('ContatoFidelizacaoForm', 'saveContato', 'Cannot save - validation failed');
    eventBus.emit('globalMessage.error', t('Por favor, preencha todos os campos obrigatórios'));
    return;
  }

  try {
    isLoading.value = true;
    log('ContatoFidelizacaoForm', 'saveContato', 'Saving contact', contatoData);

    const payload = {
      contatofidelizacao_franquia_id: Number(contatoData.contatofidelizacao_franquia_id),
      contatofidelizacao_cliente_id: Number(contatoData.contatofidelizacao_cliente_id),
      contatofidelizacao_content: contatoData.contatofidelizacao_content,
      contatofidelizacao_data_cadastro: contatoData.contatofidelizacao_data_cadastro || undefined,
      contatofidelizacao_owner: contatoData.contatofidelizacao_owner || undefined,
      contatofidelizacao_owner_id: contatoData.contatofidelizacao_owner_id || undefined,
    };
    const result = await saveContatoFidelizacao(payload);

    log('ContatoFidelizacaoForm', 'saveContato', 'Saved successfully');
    eventBus.emit('globalMessage.success', t('Contato salvo com sucesso!'));
    emit('save-success', result);

    // Trigger refresh event
    eventBus.emit('contato-fidelizacao.saved');

  } catch (error) {
    console.error('Error saving contato:', error);
    log('ContatoFidelizacaoForm', 'saveContato', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', message);
  } finally {
    isLoading.value = false;
    log('ContatoFidelizacaoForm', 'saveContato', 'Finished');
  }
};

const setDefaultDate = (): void => {
  log('ContatoFidelizacaoForm', 'setDefaultDate', 'Setting default date');
  // hora LOCAL do navegador (toISOString daria a hora de Londres)
  contatoData.contatofidelizacao_data_cadastro = fromDateToApiDateTime(new Date());
  log('ContatoFidelizacaoForm', 'setDefaultDate', 'Date set to', contatoData.contatofidelizacao_data_cadastro);
};

const resetForm = (): void => {
  log('ContatoFidelizacaoForm', 'resetForm', 'Resetting form');
  Object.assign(contatoData, {
    contatofidelizacao_id: null,
    contatofidelizacao_data_cadastro: '',
    contatofidelizacao_franquia_id: null,
    contatofidelizacao_franquia_nome: '',
    contatofidelizacao_cliente_id: null,
    cliente_nome: '',
    contatofidelizacao_owner: 'user',
    contatofidelizacao_owner_id: '',
    contatofidelizacao_content: ''
  });
};

const getSchemaToFill = (): Record<string, unknown> => {
  log('ContatoFidelizacaoForm', 'getSchemaToFill', 'Generating schema');

  const schema = {
    contatofidelizacao_data_cadastro: {
      type: 'string',
      format: 'datetime',
      description: 'Data e hora do contato no formato YYYY-MM-DD HH:MM:SS. Se não especificado, use a data/hora atual.',
      example: '2024-01-15 14:30:00'
    },
    contatofidelizacao_owner: {
      type: 'string',
      enum: ['user', 'system'],
      description: 'Quem fez o contato. Use "user" se foi um usuário humano, "system" se foi automatizado.',
      default: 'user'
    },
    contatofidelizacao_content: {
      type: 'string',
      description: 'Descrição detalhada do contato, incluindo o que foi discutido, acordos feitos, próximos passos, etc. Seja detalhado e organize em parágrafos se necessário.',
      minLength: 10
    }
  };

  log('ContatoFidelizacaoForm', 'getSchemaToFill', 'Schema', schema);
  return schema;
};

const getExtraInformations = (): Record<string, unknown> => {
  log('ContatoFidelizacaoForm', 'getExtraInformations', 'Generating extra info');

  const extraInfo = {
    cliente_nome: contatoData.cliente_nome,
    franquia_nome: contatoData.contatofidelizacao_franquia_nome,
    contexto: 'Este é um contato de fidelização com um cliente. Extraia as informações relevantes sobre o contato.',
    instrucoes: 'Se a data não for mencionada, use a data/hora atual. Se não houver indicação de quem fez o contato, assuma que foi "user".'
  };

  log('ContatoFidelizacaoForm', 'getExtraInformations', 'Extra info', extraInfo);
  return extraInfo;
};

interface AISuccessPayload {
  result: { Dados?: Partial<ContatoData> } & Partial<ContatoData> | null;
}

interface AIErrorPayload {
  error: unknown;
}

const handleAISuccess = ({ result }: AISuccessPayload): void => {
  log('ContatoFidelizacaoForm', 'handleAISuccess', 'AI extraction successful', result);
  
  try {
    // Parse the AI response and fill the form
    if (result && typeof result === 'object') {
      // Handle the response structure - could be nested in Dados
      const data = result.Dados || result;
      
      log('ContatoFidelizacaoForm', 'handleAISuccess', 'Extracted data', data);
      
      // Update form fields with extracted data
      if (data.contatofidelizacao_data_cadastro) {
        contatoData.contatofidelizacao_data_cadastro = data.contatofidelizacao_data_cadastro;
      }
      
      if (data.contatofidelizacao_owner) {
        contatoData.contatofidelizacao_owner = data.contatofidelizacao_owner;
      }
      
      if (data.contatofidelizacao_content) {
        contatoData.contatofidelizacao_content = data.contatofidelizacao_content;
      }
      
      log('ContatoFidelizacaoForm', 'handleAISuccess', 'Form updated with AI data', contatoData);
      eventBus.emit('globalMessage.success', t('Informações extraídas com sucesso!'));
    } else {
      log('ContatoFidelizacaoForm', 'handleAISuccess', 'Invalid result format', result);
      eventBus.emit('globalMessage.warning', t('Resposta da IA em formato inesperado'));
    }
  } catch (error) {
    log('ContatoFidelizacaoForm', 'handleAISuccess', 'Error processing AI result', error);
    console.error('Error processing AI result:', error);
    eventBus.emit('globalMessage.error', t('Erro ao processar resposta da IA'));
  }
};

const handleAIError = ({ error }: AIErrorPayload): void => {
  log('ContatoFidelizacaoForm', 'handleAIError', 'AI extraction failed', error);
  eventBus.emit('globalMessage.error', t('Erro ao extrair informações: ') + String(error));
};

// Expose public methods for parent components
defineExpose({
  canSave,
  loadForEdit,
  saveContato,
  resetForm,
  initializeForNew
});
</script>

<style scoped>
/* Section spacing */
.form-section {
  margin-bottom: 20px;
}

/* Form row with Flexbox */
.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 15px;
}

/* Half-width fields (2 columns) */
.form-field-half {
  flex: 1 1 calc(50% - 8px);
  min-width: 280px;
}

/* Full-width fields */
.form-field {
  margin-bottom: 20px;
}

/* Form labels */
.form-label {
  display: block;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

/* Required field marker */
.required-mark {
  color: #d9534f;
  margin-left: 2px;
}

/* Form hints */
.form-hint {
  display: block;
  margin-top: 6px;
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.4;
}

/* Responsive: Stack fields on mobile */
@media (max-width: 768px) {
  .form-field-half {
    flex: 1 1 100%;
    min-width: 0;
  }
}

/* Responsive: Smaller gap on mobile */
@media (max-width: 480px) {
  .form-row {
    gap: 10px;
  }
}
</style>

